import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Badge, Card, Input, Select, Textarea } from "../components/ui";
import AllegatiGallery from "../components/richiesteIntervento/AllegatiGallery";
import { STATO, statoMeta } from "../constants/richiesteIntervento";

const TIPOLOGIA_OPTIONS = [
  { value: 100000000, label: "Ordinario" },
  { value: 100000001, label: "Straordinario" },
];

function RichiestaRevisionePage() {
  const { id } = useParams();
  const { apiRequest, canPerformAction, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const canApprova = canPerformAction("richieste.approva");
  const canGestisci = canPerformAction("richieste.gestisci");
  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const canRevisionare = isAdmin || canApprova || canGestisci;
  const canGestisciReferenti = isAdmin || isRspp;

  const [richiesta, setRichiesta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showMotivoRifiuto, setShowMotivoRifiuto] = useState(false);
  const [motivoRifiuto, setMotivoRifiuto] = useState("");

  const [form, setForm] = useState({
    oggetto: "",
    note: "",
    dataRichiestaEsecuzione: "",
    tipologiaRichiesta: "",
    tipologiaInterventoId: "",
    fornitoreId: "",
    referenteId: "",
    emailDestinatario: "",
    referenteInternoNome: "",
    referenteInternoEmail: "",
    referenteInternoFono: "",
  });
  const [tipologie, setTipologie] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [referenti, setReferenti] = useState([]);

  const [showNuovoReferente, setShowNuovoReferente] = useState(false);
  const [savingReferente, setSavingReferente] = useState(false);
  const [nuovoReferenteForm, setNuovoReferenteForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    ruolo: "",
    principale: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest(`/richieste-intervento/${id}`);
      setRichiesta(res);
    } catch (err) {
      showToast(err.message || "Errore nel caricamento", "error");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, id, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canRevisionare) {
      showToast("Non hai i permessi per revisionare questa richiesta", "error");
      navigate(`/richieste-intervento/${id}`, { replace: true });
      return;
    }
    if (richiesta && richiesta.stato !== STATO.IN_ATTESA) {
      showToast("La richiesta non è più in attesa di approvazione", "info");
      navigate(`/richieste-intervento/${id}`, { replace: true });
    }
  }, [richiesta, canRevisionare, id, navigate, showToast]);

  const loadReferenti = async (fornitoreId) => {
    if (!fornitoreId) {
      setReferenti([]);
      return;
    }
    try {
      const res = await apiRequest(`/fornitori/${fornitoreId}/referenti`);
      setReferenti(Array.isArray(res) ? res : res.value || []);
    } catch {
      setReferenti([]);
    }
  };

  useEffect(() => {
    if (!richiesta) return;

    setForm({
      oggetto: richiesta.oggetto || "",
      note: richiesta.note || "",
      dataRichiestaEsecuzione: richiesta.dataRichiestaEsecuzione
        ? new Date(richiesta.dataRichiestaEsecuzione)
            .toISOString()
            .split("T")[0]
        : "",
      tipologiaRichiesta: richiesta.tipologiaRichiesta ?? "",
      tipologiaInterventoId: richiesta.tipologiaIntervento?.id || "",
      fornitoreId: richiesta.fornitore?.id || "",
      referenteId: richiesta.referente?.id || "",
      emailDestinatario: richiesta.emailDestinatario || "",
      referenteInternoNome: richiesta.referenteInterno?.nome || "",
      referenteInternoEmail: richiesta.referenteInterno?.email || "",
      referenteInternoFono: richiesta.referenteInterno?.telefono || "",
    });

    (async () => {
      try {
        const res = await apiRequest("/tipologie-intervento");
        const tutte = res?.value || [];
        const generiche = tutte.filter((t) => t.generica);
        const altre = tutte
          .filter((t) => !t.generica)
          .sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it"));
        setTipologie([...altre, ...generiche]);
      } catch {
        setTipologie([]);
      }
    })();

    (async () => {
      try {
        const res = richiesta.rsa?.id
          ? await apiRequest(`/rsa/${richiesta.rsa.id}/fornitori`)
          : await apiRequest("/fornitori");
        let lista = res?.value || [];
        if (!lista.length && richiesta.rsa?.id) {
          const tutti = await apiRequest("/fornitori");
          lista = tutti?.value || [];
        }
        setFornitori(lista);
      } catch {
        setFornitori([]);
      }
    })();

    loadReferenti(richiesta.fornitore?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [richiesta]);

  const handleFornitoreChange = (fornitoreId) => {
    setForm((f) => ({
      ...f,
      fornitoreId,
      referenteId: "",
      emailDestinatario: "",
    }));
    setShowNuovoReferente(false);
    loadReferenti(fornitoreId);
  };

  const apriNuovoReferente = () => {
    setNuovoReferenteForm({
      nome: "",
      cognome: "",
      email: "",
      telefono: "",
      ruolo: "",
      principale: false,
    });
    setShowNuovoReferente(true);
  };

  const salvaNuovoReferente = async () => {
    if (!nuovoReferenteForm.nome && !nuovoReferenteForm.cognome) {
      showToast("Nome o cognome del referente obbligatorio", "error");
      return;
    }
    setSavingReferente(true);
    try {
      const res = await apiRequest(
        `/admin/fornitori/${form.fornitoreId}/referenti`,
        {
          method: "POST",
          body: JSON.stringify(nuovoReferenteForm),
        },
      );
      showToast("Referente creato", "success");
      await loadReferenti(form.fornitoreId);
      setForm((f) => ({ ...f, referenteId: res.id }));
      setShowNuovoReferente(false);
    } catch (err) {
      showToast(err.message || "Errore nella creazione del referente", "error");
    } finally {
      setSavingReferente(false);
    }
  };

  const fornitoreSelezionato = fornitori.find((f) => f.id === form.fornitoreId);
  const referenteSelezionato = referenti.find((r) => r.id === form.referenteId);
  const fornitoreSenzaEmail =
    !!form.fornitoreId &&
    fornitoreSelezionato &&
    !fornitoreSelezionato.email &&
    !fornitoreSelezionato.emailOperativa &&
    !fornitoreSelezionato.emailReferente;
  const emailInvioEffettiva =
    referenteSelezionato?.email ||
    form.emailDestinatario ||
    fornitoreSelezionato?.email ||
    fornitoreSelezionato?.emailOperativa ||
    fornitoreSelezionato?.emailReferente;

  const buildPayload = () => ({
    oggetto: form.oggetto || undefined,
    note: form.note,
    dataRichiestaEsecuzione: form.dataRichiestaEsecuzione || "",
    tipologiaRichiesta:
      form.tipologiaRichiesta !== ""
        ? Number(form.tipologiaRichiesta)
        : undefined,
    tipologiaInterventoId: form.tipologiaInterventoId || undefined,
    fornitoreId: form.fornitoreId || "",
    referenteId: form.referenteId || "",
    emailDestinatario: form.emailDestinatario,
    referenteInternoNome: form.referenteInternoNome,
    referenteInternoEmail: form.referenteInternoEmail,
    referenteInternoFono: form.referenteInternoFono,
  });

  const validaEmailDestinatario = () => {
    if (
      fornitoreSenzaEmail &&
      !referenteSelezionato?.email &&
      !form.emailDestinatario.trim()
    ) {
      showToast(
        "Il fornitore selezionato non ha email in anagrafica: seleziona un referente con email o specifica un indirizzo email destinatario",
        "error",
      );
      return false;
    }
    return true;
  };

  const salvaBozza = async () => {
    if (!validaEmailDestinatario()) return;
    setSaving(true);
    try {
      await apiRequest(`/richieste-intervento/${id}`, {
        method: "PATCH",
        body: JSON.stringify(buildPayload()),
      });
      showToast("Modifiche salvate", "success");
      await load();
    } catch (err) {
      showToast(err.message || "Errore nel salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const approva = async () => {
    if (!validaEmailDestinatario()) return;
    setApproving(true);
    try {
      await apiRequest(`/richieste-intervento/${id}`, {
        method: "PATCH",
        body: JSON.stringify(buildPayload()),
      });
      const res = await apiRequest(`/richieste-intervento/${id}/approva`, {
        method: "PATCH",
        body: JSON.stringify({}),
      });
      if (res?.emailInviata) {
        showToast(
          "Richiesta approvata ed email inviata al fornitore",
          "success",
        );
      } else {
        showToast(
          "Richiesta approvata. Attenzione: email al fornitore non inviata.",
          "warning",
        );
      }
      navigate(`/richieste-intervento/${id}`);
    } catch (err) {
      showToast(err.message || "Errore nell'approvazione", "error");
    } finally {
      setApproving(false);
    }
  };

  const rifiuta = async () => {
    if (!motivoRifiuto.trim()) {
      showToast("Il motivo del rifiuto è obbligatorio", "error");
      return;
    }
    setRejecting(true);
    try {
      await apiRequest(`/richieste-intervento/${id}/rifiuta`, {
        method: "PATCH",
        body: JSON.stringify({ motivo: motivoRifiuto }),
      });
      showToast("Richiesta rifiutata", "success");
      navigate(`/richieste-intervento/${id}`);
    } catch (err) {
      showToast(err.message || "Errore nel rifiuto", "error");
    } finally {
      setRejecting(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Caricamento…</div>;
  if (!richiesta)
    return <div className="p-6 text-gray-500">Richiesta non trovata.</div>;
  if (!canRevisionare || richiesta.stato !== STATO.IN_ATTESA) return null;

  const meta = statoMeta(richiesta.stato);
  const acting = saving || approving || rejecting;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <button
        onClick={() => navigate(`/richieste-intervento/${id}`)}
        className="text-sm text-gray-500 hover:underline mb-4"
      >
        ← Torna al dettaglio
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Revisione richiesta — {richiesta.oggetto}
        </h1>
        <div className="mt-2">
          <Badge variant={meta.variant}>{meta.label}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonna principale */}
        <div className="lg:col-span-2 space-y-4">
          <Card title="Dati richiesta">
            <div className="mb-4">
              <Input
                label="Oggetto"
                value={form.oggetto}
                onChange={(e) =>
                  setForm((f) => ({ ...f, oggetto: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <div className="flex py-2 border-b border-gray-100">
                <div className="w-56 font-medium text-gray-500">RSA</div>
                <div className="flex-1 text-gray-900">
                  {richiesta.rsa?.nome}
                </div>
              </div>
              <div className="flex py-2 border-b border-gray-100">
                <div className="w-56 font-medium text-gray-500">
                  Richiedente
                </div>
                <div className="flex-1 text-gray-900">
                  {richiesta.richiedente}
                </div>
              </div>
              <div className="flex py-2">
                <div className="w-56 font-medium text-gray-500">
                  Descrizione intervento
                </div>
                <div className="flex-1 text-gray-900 whitespace-pre-wrap">
                  {richiesta.note || "—"}
                </div>
              </div>
            </div>
          </Card>

          <AllegatiGallery
            richiestaId={richiesta.id}
            allegati={richiesta.allegati}
          />

          <Card title="Tipologia">
            <div className="space-y-4">
              <Select
                label="Tipologia richiesta"
                value={form.tipologiaRichiesta}
                onChange={(e) =>
                  setForm((f) => ({ ...f, tipologiaRichiesta: e.target.value }))
                }
              >
                <option value="">Seleziona…</option>
                {TIPOLOGIA_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <Select
                label="Tipologia intervento"
                value={form.tipologiaInterventoId}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    tipologiaInterventoId: e.target.value,
                  }))
                }
              >
                <option value="">Seleziona…</option>
                {tipologie.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nome}
                  </option>
                ))}
              </Select>
            </div>
          </Card>

          <Card title="Assegnazione fornitore">
            <div className="space-y-4">
              <Select
                label="Fornitore"
                value={form.fornitoreId}
                onChange={(e) => handleFornitoreChange(e.target.value)}
              >
                <option value="">Nessuno</option>
                {fornitori.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                    {!f.email && !f.emailOperativa && !f.emailReferente
                      ? " ⚠ nessuna email"
                      : ""}
                  </option>
                ))}
              </Select>
              {fornitoreSenzaEmail && (
                <Input
                  type="email"
                  label="Email destinatario *"
                  value={form.emailDestinatario}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      emailDestinatario: e.target.value,
                    }))
                  }
                  placeholder="email@fornitore.com"
                  helper="Il fornitore selezionato non ha email in anagrafica: indicane una per l'invio della richiesta."
                />
              )}
              {form.fornitoreId && (
                <div
                  className={`text-sm rounded-lg border p-3 ${
                    emailInvioEffettiva
                      ? "border-blue-200 bg-blue-50 text-blue-800"
                      : "border-red-200 bg-red-50 text-red-800"
                  }`}
                >
                  {emailInvioEffettiva ? (
                    referenteSelezionato?.email ? (
                      <>
                        📧 L'email verrà indirizzata al referente{" "}
                        <strong>
                          {referenteSelezionato.nomeCompleto ||
                            `${referenteSelezionato.nome || ""} ${referenteSelezionato.cognome || ""}`.trim()}
                        </strong>{" "}
                        ({emailInvioEffettiva}), non all'indirizzo aziendale.
                      </>
                    ) : (
                      <>
                        📧 L'email verrà indirizzata a{" "}
                        <strong>{fornitoreSelezionato?.nome}</strong> (
                        {emailInvioEffettiva}).
                      </>
                    )
                  ) : (
                    <>
                      ⚠ Nessun indirizzo email disponibile per l'invio: indica
                      un'email destinatario qui sopra prima di approvare.
                    </>
                  )}
                </div>
              )}
              {form.fornitoreId && (
                <div className="space-y-2">
                  <Select
                    label="Persona di riferimento presso il fornitore (opzionale)"
                    value={form.referenteId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, referenteId: e.target.value }))
                    }
                    helper={
                      referenti.length === 0
                        ? `Nessun contatto nominativo collegato: l'email verrà indirizzata a ${fornitoreSelezionato?.nome || "il fornitore"}.`
                        : "Se il contatto selezionato ha un'email, la richiesta verrà inviata direttamente a lui/lei invece che all'indirizzo aziendale."
                    }
                  >
                    <option value="">Nessuno</option>
                    {referenti.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.nomeCompleto ||
                          `${r.nome || ""} ${r.cognome || ""}`.trim()}
                        {r.ruolo ? ` — ${r.ruolo}` : ""}
                        {r.email ? ` — ${r.email}` : ""}
                        {r.telefono ? ` — ${r.telefono}` : ""}
                      </option>
                    ))}
                  </Select>

                  {form.referenteId &&
                    (() => {
                      const sel = referenti.find(
                        (r) => r.id === form.referenteId,
                      );
                      if (!sel) return null;
                      return (
                        <div className="text-xs text-gray-500 pl-1">
                          {sel.email && <span>{sel.email}</span>}
                          {sel.telefono && (
                            <span>
                              {sel.email ? " · " : ""}
                              {sel.telefono}
                            </span>
                          )}
                        </div>
                      );
                    })()}

                  {canGestisciReferenti && !showNuovoReferente && (
                    <button
                      type="button"
                      onClick={apriNuovoReferente}
                      className="text-sm text-primary-600 hover:underline"
                    >
                      + Aggiungi nuovo contatto al fornitore
                    </button>
                  )}

                  {showNuovoReferente && (
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          label="Nome"
                          value={nuovoReferenteForm.nome}
                          onChange={(e) =>
                            setNuovoReferenteForm((f) => ({
                              ...f,
                              nome: e.target.value,
                            }))
                          }
                        />
                        <Input
                          label="Cognome"
                          value={nuovoReferenteForm.cognome}
                          onChange={(e) =>
                            setNuovoReferenteForm((f) => ({
                              ...f,
                              cognome: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <Input
                        type="email"
                        label="Email"
                        value={nuovoReferenteForm.email}
                        onChange={(e) =>
                          setNuovoReferenteForm((f) => ({
                            ...f,
                            email: e.target.value,
                          }))
                        }
                        placeholder="email@esempio.com"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="tel"
                          label="Telefono"
                          value={nuovoReferenteForm.telefono}
                          onChange={(e) =>
                            setNuovoReferenteForm((f) => ({
                              ...f,
                              telefono: e.target.value,
                            }))
                          }
                          placeholder="+39 ..."
                        />
                        <Input
                          label="Ruolo"
                          value={nuovoReferenteForm.ruolo}
                          onChange={(e) =>
                            setNuovoReferenteForm((f) => ({
                              ...f,
                              ruolo: e.target.value,
                            }))
                          }
                          placeholder="es. Responsabile tecnico"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="nuovo-referente-principale"
                          checked={nuovoReferenteForm.principale}
                          onChange={(e) =>
                            setNuovoReferenteForm((f) => ({
                              ...f,
                              principale: e.target.checked,
                            }))
                          }
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="nuovo-referente-principale"
                          className="text-sm text-gray-700"
                        >
                          Referente principale
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowNuovoReferente(false)}
                          disabled={savingReferente}
                        >
                          Annulla
                        </Button>
                        <Button
                          size="sm"
                          onClick={salvaNuovoReferente}
                          loading={savingReferente}
                        >
                          Salva contatto
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <Input
                type="date"
                label="Data richiesta di esecuzione (opzionale)"
                value={form.dataRichiestaEsecuzione}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    dataRichiestaEsecuzione: e.target.value,
                  }))
                }
              />
            </div>
          </Card>

          <Card title="Referente Peralba da contattare (opzionale)">
            <p className="text-xs text-gray-500 -mt-2 mb-3">
              Facoltativo: se compilato, verrà incluso nell'email inviata al
              fornitore come persona interna da contattare.
            </p>
            <div className="space-y-4">
              <Input
                label="Nome referente"
                value={form.referenteInternoNome}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    referenteInternoNome: e.target.value,
                  }))
                }
                placeholder="Nome e cognome"
              />
              <Input
                type="email"
                label="Email referente"
                value={form.referenteInternoEmail}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    referenteInternoEmail: e.target.value,
                  }))
                }
                placeholder="referente@peralbahc.com"
              />
              <Input
                type="tel"
                label="Telefono referente"
                value={form.referenteInternoFono}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    referenteInternoFono: e.target.value,
                  }))
                }
                placeholder="+39 000 0000000"
              />
            </div>
          </Card>

          <Card title="Descrizione">
            <Textarea
              label="Descrizione Intervento"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              rows={4}
            />
          </Card>
        </div>

        {/* Sidebar azioni */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-6 space-y-4">
            <Card title="Azioni">
              <div className="space-y-3">
                <Button
                  fullWidth
                  onClick={approva}
                  loading={approving}
                  disabled={acting}
                >
                  Approva e invia al fornitore
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  onClick={salvaBozza}
                  loading={saving}
                  disabled={acting}
                >
                  Salva modifiche
                </Button>
                {!showMotivoRifiuto ? (
                  <Button
                    fullWidth
                    variant="danger"
                    onClick={() => setShowMotivoRifiuto(true)}
                    disabled={acting}
                  >
                    Rifiuta richiesta
                  </Button>
                ) : (
                  <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-800">
                      Il rifiuto è irreversibile. Specifica il motivo.
                    </p>
                    <Textarea
                      value={motivoRifiuto}
                      onChange={(e) => setMotivoRifiuto(e.target.value)}
                      rows={3}
                      placeholder="Motivo del rifiuto…"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setShowMotivoRifiuto(false);
                          setMotivoRifiuto("");
                        }}
                        disabled={acting}
                      >
                        Annulla
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={rifiuta}
                        loading={rejecting}
                        disabled={acting || !motivoRifiuto.trim()}
                      >
                        Conferma rifiuto
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RichiestaRevisionePage;
